import { defineStore } from 'pinia';
import router from '@/router';
import { getUserInfo, login, getPageBtnPermissionByRoleId } from '@/api/auth';
import { storage } from '@/utils/storage';
import { useNavTabStore } from './navTab';
import { useRouteStore } from './route';
import { t } from '@/hooks/useI18n';

interface IUserState {
  token: string | null;
  userInfo: {
    userName: string;
    role: string;
  };
  permissions: string[];
  btns: string[];
  firstMenu: string;
}

export const useUserStore = defineStore({
  id: 'user',
  state: (): IUserState => ({
    token: storage.get('token'),
    userInfo: storage.get('userInfo') || { userName: '', role: '' },
    permissions: storage.get('permissions') || [],
    btns: storage.get('btns') || [],
    firstMenu: '/'
  }),
  getters: {
    isLogin(state) {
      return Boolean(state.token);
    }
  },
  actions: {
    resetUser() {
      const navTabStore = useNavTabStore();
      const routeStore = useRouteStore();
      storage.remove('userInfo');
      storage.remove('token');
      storage.remove('permissions');
      storage.remove('btns');
      navTabStore.reset();
      routeStore.setIsInitRoute(false);
      this.$reset();
    },
    setToken(token: string) {
      this.token = token;
      storage.set('token', token);
    },
    async setUserInfo() {
      const { data } = await getUserInfo();
      if (data?.menuList?.length) {
        this.setPermissions(data.menuList);
      } else {
        ElMessage.error(t('login.accountNoPermissonHint'));
        this.resetUser();
        setTimeout(() => {
          router.push('/login');
        }, 1000);

        return;
      }
  
      // 查询是否包含首页(分析页)权限[import.meta.env.VITE_ROUTE_HOME_PATH]，如果包含就加载首页，如果不包含，则加载第一个菜单权限
      for (let i = 0; i < data.menuList.length; i++) {
        const tmpMenuItem = data.menuList[i];
        if (!tmpMenuItem.chileMenuList || !tmpMenuItem.chileMenuList.length) {
          continue;
        }

        let isOver = false;
        for (let j = 0; j < tmpMenuItem.chileMenuList.length; j++) {
          const tmpChildMenuItem = tmpMenuItem.chileMenuList[j];
          // 如果有首页(分析页)权限, 直接跳出两层循环
          if (
            tmpChildMenuItem.resourceUrl &&
            tmpChildMenuItem.resourceUrl == import.meta.env.VITE_ROUTE_HOME_PATH
          ) {
            this.firstMenu = '/';
            isOver = true;
            break;
          }

          // 把第一个菜单放入this.firstMenu，没有首页就加载第一个菜单
          if (tmpChildMenuItem.resourceUrl && this.firstMenu == '/') {
            this.firstMenu = tmpChildMenuItem.resourceUrl;
          }
        }

        //有首页时跳出循序
        if (isOver) {
          break;
        }
      }

      this.userInfo = data;
      storage.set('userInfo', data);
      if (data.roleList?.length) {
        const roleIds = data.roleList.map((item) => item.roleId);
        const res = await getPageBtnPermissionByRoleId({ rolesIds: roleIds });
        if (res?.data?.length) {
          const btns = res.data.map((item) => item.resourceKey);
          this.setBtns(btns);
        }
      }
    },

    setPermissions(permissions: string[]) {
      this.permissions = permissions;
      storage.set('permissions', permissions);
    },
    setBtns(btns: string[]) {
      this.btns = btns;
      storage.set('btns', btns);
    },
    async login(token: string) {
      //params: any const { data } = await login(params);
      this.setToken(token);
      await this.setUserInfo();

      router.push((router.currentRoute.value.query?.redirect || this.firstMenu) as string);

      ElNotification({
        title: t('login.loginSuccess'),
        type: 'success',
        message: t('login.welcomeBack') + `，${this.userInfo.userName}`
      });
    },
    logout() {
      ElMessageBox.confirm(t('login.loginOutHint'), t('setting.tips'))
        .then(() => {
          this.resetUser();
          router.push('/login');
        })
        .catch(() => {});
    }
  }
});
